import { logError, normalizeError } from '@/shared/lib/errors';
import type { IScheduleDay, ITimeSlot } from '@/entities/appointment/model/types';
import type { IThreeDaysData } from '@/pages/lawyer/model/types/threeDays';

export const useThreeDays = (lawyerId: Ref<number | undefined>) => {
  const { $appointmentService } = useNuxtApp();

  const { data, pending } = useAsyncData<IThreeDaysData>(
    () => `three-days:${lawyerId.value ?? 'none'}`,
    async () => {
      const id = lawyerId.value;
      if (!id) return { days: [], defaultSlots: [] };

      let days: IScheduleDay[] = [];
      try {
        days = (await $appointmentService.getThreeScheduleDays(id)).data ?? [];
      } catch (error) {
        logError(normalizeError(error), { action: 'three-days-schedule' });
        return { days: [], defaultSlots: [] };
      }

      const firstDate = days[0]?.date;
      if (!firstDate) return { days, defaultSlots: [] };

      let defaultSlots: ITimeSlot[] = [];
      try {
        const date = new Date(firstDate);
        defaultSlots =
          (
            await $appointmentService.getScheduleDay(
              id,
              date.getFullYear(),
              date.getMonth() + 1,
              date.getDate(),
            )
          ).data ?? [];
      } catch (error) {
        logError(normalizeError(error), { action: 'lawyer-default-slots' });
      }

      return { days, defaultSlots };
    },
    {
      default: (): IThreeDaysData => ({ days: [], defaultSlots: [] }),
      watch: [() => lawyerId.value],
    },
  );

  const days: ComputedRef<IScheduleDay[]> = computed(() => data.value.days);
  const defaultSlots: ComputedRef<ITimeSlot[]> = computed(() => data.value.defaultSlots);

  return { days, defaultSlots, pending };
};

export default useThreeDays;
